"use client";

import { useEffect, useRef, useState } from "react";
import { packages } from "../_data";
import { track } from "../_analytics/track";

/**
 * Het rekenvoorbeeld stond hier als vaste tabel voor één woning van 750 m³.
 * Nu vult de bezoeker zijn eigen inhoud in en rekenen we met dezelfde
 * richtprijzen per m³ die ook op de pakketkaarten hierboven staan.
 *
 * Meten: de eerste wijziging is `price_calculator_started`, een getoond bedrag
 * is `price_calculator_completed`. Dat tweede event wacht even: wie 750 intypt
 * passeert onderweg 7 en 75, en alleen het bedrag waar hij bij blijft telt.
 */

const MIN_VOLUME = 100;
const MAX_VOLUME = 3000;
const DEFAULT_VOLUME = 750;

const euro = new Intl.NumberFormat("nl-NL", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

/** Afgerond op vijfduizend euro: dit is een richting, geen offertebedrag. */
function indication(volume: number, rate: number) {
  return euro.format(Math.round((volume * rate) / 5000) * 5000);
}

export function PriceCalculator() {
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const [touched, setTouched] = useState(false);
  const gestart = useRef(false);
  const gemeldVolume = useRef<number | null>(null);

  function handleVolume(value: string) {
    const parsed = Number.parseInt(value, 10);
    if (!gestart.current) {
      gestart.current = true;
      track("price_calculator_started", { component: "pk-calc" });
    }
    setTouched(true);
    setVolume(Number.isNaN(parsed) ? 0 : parsed);
  }

  const valid = volume >= MIN_VOLUME && volume <= MAX_VOLUME;

  // Het bedrag staat er; wacht tot de bezoeker klaar is met typen en meld dan
  // één keer per volume. `pakket` blijft leeg: alle drie de bedragen staan
  // tegelijk in beeld, dus de bezoeker koos hier nog geen pakket.
  useEffect(() => {
    if (!gestart.current || !valid || gemeldVolume.current === volume) return;
    const wachten = setTimeout(() => {
      gemeldVolume.current = volume;
      track("price_calculator_completed", { component: "pk-calc", volume_m3: volume });
    }, 800);
    return () => clearTimeout(wachten);
  }, [valid, volume]);

  return (
    <div className="pk-calc">
      <label className="pk-calc-field">
        Hoeveel kubieke meter woninginhoud?
        <span className="pk-calc-input">
          <input
            type="number"
            inputMode="numeric"
            min={MIN_VOLUME}
            max={MAX_VOLUME}
            step={10}
            value={volume === 0 ? "" : volume}
            onChange={(event) => handleVolume(event.target.value)}
            aria-describedby="pk-calc-help"
          />
          <span aria-hidden="true">m³</span>
        </span>
      </label>
      <p className="pk-calc-help" id="pk-calc-help">
        Woninginhoud, niet woonoppervlak. Een ruime vrijstaande woning zit rond de 750 m³. Reken tussen {MIN_VOLUME} en{" "}
        {MAX_VOLUME} m³.
      </p>

      <div className="pk-example" aria-live="polite">
        {valid ? (
          packages.map((pack) => (
            <div key={pack.name}>
              <span>
                {pack.name} · {volume} m³ × {pack.price.replace(" per m³", "")}
              </span>
              <strong>{indication(volume, pack.pricePerM3)}</strong>
            </div>
          ))
        ) : (
          <div>
            <span>Nog geen bedrag</span>
            <strong>
              {touched ? `Vul ${MIN_VOLUME} tot ${MAX_VOLUME} m³ in` : `Vul je woninginhoud in`}
            </strong>
          </div>
        )}
      </div>
    </div>
  );
}
